import { useEffect, useState } from 'react';

export interface EditorSessionData {
  imageUrl: string;
  instruction: string;
  preset: string;
}

export interface UseSessionResult {
  data: EditorSessionData | null;
  isLoading: boolean;
  error: string | null;
}

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const ERROR_MESSAGES = new Map<string, string>([
  ['EDIT_SESSION_EXPIRED', 'This editing session has expired. Please request a new one.'],
  ['EDIT_SESSION_NOT_FOUND', 'Editing session not found. It may have already been used.'],
  ['EDIT_NONCE_INVALID', 'This link has already been used. Please request a new editing session.'],
  ['EDIT_HMAC_MISMATCH', 'Invalid session link. Please request a new editing session.'],
]);

// Module-level dedupe so a StrictMode double-mount (or any remount with the
// same nonce) reuses the original in-flight request. The session-validate
// endpoint consumes the nonce atomically — a second fetch would always 403
// with EDIT_NONCE_INVALID even though the first call succeeded.
type ValidateOutcome = { ok: true; data: EditorSessionData } | { ok: false; error: string };
const inflight = new Map<string, Promise<ValidateOutcome>>();

function runValidate(
  sessionId: string,
  token: string,
  expires: string,
  nonce: string,
): Promise<ValidateOutcome> {
  const existing = inflight.get(nonce);
  if (existing) return existing;

  const promise = (async (): Promise<ValidateOutcome> => {
    try {
      const res = await fetch(`${API_BASE}/v1/edit/session/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, token, expires, nonce }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { error?: { code?: string; message?: string } };
        const code = body.error?.code ?? '';
        return {
          ok: false,
          error: ERROR_MESSAGES.get(code) ?? body.error?.message ?? 'Failed to validate session.',
        };
      }

      const body = (await res.json()) as { data: EditorSessionData };
      return { ok: true, data: body.data };
    } catch {
      return { ok: false, error: 'Network error. Please check your connection and try again.' };
    }
  })();

  inflight.set(nonce, promise);
  return promise;
}

export function useSession(
  sessionId: string | undefined,
  token: string | null,
  expires: string | null,
  nonce: string | null,
): UseSessionResult {
  const [data, setData] = useState<EditorSessionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId || !token || !expires || !nonce) {
      setError('Missing session parameters. Please use a valid editor link.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    runValidate(sessionId, token, expires, nonce).then((outcome) => {
      if (cancelled) return;
      if (outcome.ok) {
        setData(outcome.data);
      } else {
        setError(outcome.error);
      }
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [sessionId, token, expires, nonce]);

  return { data, isLoading, error };
}
